/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import type { Cart } from "@packages/sdk";
import type { ThemeType } from "@packages/ui/emotion";
import { Text } from "@packages/ui/emotion";
import { HallowDataTestId } from "@tools/playwright/global";
import { useMemo } from "react";

import {
  DAYS_MIN_FOR_MONTHLY_TEXT,
  LIFETIME_DAYS,
  MONTH_DAYS,
  YEAR_DAYS,
} from "../src/lib/constants/days.constants";
import { HallowCartGrant } from "../src/types/enums/hallowCartGrant.types";
import { HallowCartRule } from "../src/types/enums/hallowCartRule.types";
import { HallowPromoCodeGroup } from "../src/types/enums/hallowPromoCodeGroup.types";
import type { Payment } from "../src/types/payment.types";
import { getCartGrant } from "../src/utils/getCartGrant";
import { getCartRule } from "../src/utils/getCartRule";

type PromotionalOfferProps = {
  amounts: Payment["amounts"];
  cart: Cart;
  getNumberFormatted: Payment["getNumberFormatted"];
  isPaymentRequired: Payment["isPaymentRequired"];
  trialDiscountPercent: Payment["trialDiscountPercent"];
  trialEndDate: Payment["trialEndDate"];
  trialLength: Payment["trialLength"];
  trialType: Payment["trialType"];
};

const cssBanner: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.accentMedHigh,
  borderRadius: theme.radius.s,
  color: theme.colors.white,
  fontWeight: 600,
  paddingBottom: theme.spacing.xs,
  paddingLeft: theme.spacing.ms,
  paddingRight: theme.spacing.ms,
  paddingTop: theme.spacing.xs,
  position: "absolute",
  textAlign: "center",
  textTransform: "uppercase",
  top: -8,
});
const cssContainer: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: theme.semanticColors.accentLowest,
  borderRadius: theme.radius.ms,
  color: theme.semanticColors.accentMedHigh,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.xxs,
  paddingBottom: theme.spacing.l,
  paddingLeft: theme.spacing.m,
  paddingRight: theme.spacing.m,
  paddingTop: theme.spacing.xl,
  position: "relative",
  textAlign: "center",
  width: "100%",
});

export const PromotionalOffer = ({
  amounts,
  cart,
  getNumberFormatted,
  isPaymentRequired,
  trialDiscountPercent,
  trialEndDate,
  trialLength,
  trialType,
}: PromotionalOfferProps) => {
  const t = useTranslations();

  const isShowMonthly = useMemo(
    () =>
      trialLength > DAYS_MIN_FOR_MONTHLY_TEXT && trialLength % MONTH_DAYS === 0,
    [trialLength],
  );
  const isShowYearly = useMemo(
    () => trialLength % YEAR_DAYS === 0,
    [trialLength],
  );
  const isYearTrial = useMemo(() => trialLength === YEAR_DAYS, [trialLength]);
  /** Grants */
  const discountGrantForever = useMemo(
    () =>
      getCartGrant({
        cart,
        grant: HallowCartGrant.DiscountGrant,
        filter: (item) => item.type === "percent",
      })?.variables?.application_type === "forever",
    [cart],
  );
  const trialEndDateGrant = useMemo(
    () => getCartGrant({ cart, grant: HallowCartGrant.TrialEndDate }),
    [cart],
  );
  /** Rules */
  const hadPreviousTrialRule = useMemo(
    () => getCartRule({ cart, rule: HallowCartRule.HadPreviousTrial }),
    [cart],
  );

  const banner = useMemo(() => {
    if (cart?.promo_code?.group === HallowPromoCodeGroup.GiftCard)
      return t("web_payments_gift_card");
    else return t("web_payments_promotional_offer");
  }, [cart]);
  const description = useMemo(() => {
    if (hadPreviousTrialRule)
      return t("redeem_landing_condition_new_subscriber");

    return null;
  }, [hadPreviousTrialRule]);
  const subtitle = useMemo(() => {
    if (!isPaymentRequired) return t("redeem_landing_condition_no_cc");

    if (cart?.discount && trialType) {
      /** Intro offers AND discounts OR yearly subscriptions AND discount */
      if (
        (cart?.intro_offer && trialDiscountPercent > 0) ||
        cart?.product?.billing_period?.unit === "year"
      ) {
        const message = discountGrantForever
          ? "checkout_summary_then_discount"
          : isYearTrial
            ? "checkout_summary_then_second_year_discount"
            : "checkout_summary_then_first_year_discount";
        return t.rich(message, {
          0: getNumberFormatted.format(cart?.subtotal),
          1: getNumberFormatted.format(amounts.afterTrialPreTax),
          2: trialDiscountPercent,
          s: (chunks) => <s>{chunks}</s>,
        });
      }
    }

    if (cart?.product?.billing_period?.unit === "year")
      return t("checkout_summary_then_billed_annually", {
        0: getNumberFormatted.format(amounts.renewalPreTax),
      });

    return t("checkout_summary_then_billed_monthly", {
      0: getNumberFormatted.format(amounts.renewalPreTax),
    });
  }, [amounts, cart, isPaymentRequired]);
  const title = useMemo(() => {
    /** If one-time intro offer, display appropriate title */
    if (cart?.intro_offer) {
      if (trialEndDateGrant)
        return t("redeem_landing_one_time_payment", {
          0: amounts.todayPreTax,
          1: trialEndDate,
        });
      else
        return t("redeem_landing_one_time_payment_for", {
          0: amounts.todayPreTax,
          1: isShowMonthly
            ? t("plural_months", {
                plural_months: Math.round(trialLength / MONTH_DAYS),
              })
            : t("plural_days", { plural_days: trialLength }),
        });
    }

    /** If trial length is more than 5 years, display lifetime subscription title */
    if (trialLength >= LIFETIME_DAYS)
      return t("subscription_title_lifetime_subscription");

    /** If specified end date, display appropriate title */
    if (trialEndDateGrant) {
      if (isPaymentRequired)
        return t("redeem_landing_trial_end_date", { 0: trialEndDate });
      else return t("redeem_landing_trial_end_date_free", { 0: trialEndDate });
    }

    /** If trial, display appropriate title */
    if (trialLength > 0) {
      if (isShowYearly)
        return t("plural_subscription_year_trial", {
          plural_subscription_year_trial: Math.round(trialLength / YEAR_DAYS),
        });
      else if (isShowMonthly)
        return t("plural_subscription_month_trial", {
          plural_subscription_month_trial: Math.round(trialLength / MONTH_DAYS),
        });
      else
        return t("plural_subscription_day_trial", {
          plural_subscription_day_trial: trialLength,
        });
    }

    if (isShowYearly)
      return t("plural_checkout_summary_years_for", {
        plural_checkout_summary_years_for:
          cart?.product?.billing_period?.number,
        "1": getNumberFormatted.format(amounts.todayPreTax),
      });
    else if (isShowMonthly)
      return t("plural_checkout_summary_months_for", {
        plural_checkout_summary_months_for:
          cart?.product?.billing_period?.number,
        "1": getNumberFormatted.format(amounts.todayPreTax),
      });
    else
      return t("plural_subscription_day_trial", {
        plural_subscription_day_trial: trialLength,
      });
  }, [
    amounts,
    cart,
    isPaymentRequired,
    isShowMonthly,
    isShowYearly,
    trialEndDateGrant,
    trialLength,
  ]);

  if (cart)
    return (
      <div data-testid={HallowDataTestId.PromotionalOffer} css={cssContainer}>
        <Text as="div" css={cssBanner} size="l" type="detail">
          {banner}
        </Text>
        <Text size="l" type="headline">
          {title}
        </Text>
        <Text as="p" size="m" type="body">
          {subtitle}
        </Text>
        {description && (
          <Text as="p" size="s" type="body">
            {description}
          </Text>
        )}
      </div>
    );
};
